import React, { createContext, useContext, useEffect, useState } from 'react';
import { sign_in, sign_up, refresh_token, refresh_band_permissions, AuthResponse, SignUpData } from '../api';

const AUTH_STORAGE_KEY = 'spec_auth';

interface StoredAuth {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  token: string;
  refreshToken: string;
  claims: AuthResponse['claims'];
}

interface AuthUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  claims: AuthResponse['claims'];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData) => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to get stored auth
const getStoredAuth = (): StoredAuth | null => {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

// Helper to store auth
const storeAuth = (auth: StoredAuth) => {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
};

// Helper to clear auth
const clearAuth = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for stored auth on mount and validate token
  useEffect(() => {
    const validateAndRestoreAuth = async () => {
      const stored = getStoredAuth();
      if (!stored) {
        setLoading(false);
        return;
      }

      // Try to refresh the token to validate it's still good
      try {
        const response = await refresh_token(stored.refreshToken);

        // Token is valid, update stored auth with fresh token
        storeAuth({
          uid: response.uid,
          email: response.email,
          displayName: response.displayName,
          photoURL: response.photoURL,
          token: response.token,
          refreshToken: response.refreshToken,
          claims: response.claims,
        });

        setUser({
          uid: response.uid,
          email: response.email,
          displayName: response.displayName,
          photoURL: response.photoURL,
          claims: response.claims,
        });
      } catch (error) {
        // Token refresh failed - clear auth and force re-login
        console.warn('Token validation failed, clearing auth:', error);
        clearAuth();
        setUser(null);
      }

      setLoading(false);
    };

    validateAndRestoreAuth();
  }, []);

  // Listen for auth:expired events from API calls
  useEffect(() => {
    const handleAuthExpired = () => {
      console.warn('Auth expired event received, signing out');
      clearAuth();
      setUser(null);
    };

    window.addEventListener('auth:expired', handleAuthExpired);
    return () => window.removeEventListener('auth:expired', handleAuthExpired);
  }, []);

  const signIn = async (email: string, password: string) => {
    const response = await sign_in(email, password);

    // Store auth data
    storeAuth({
      uid: response.uid,
      email: response.email,
      displayName: response.displayName,
      photoURL: response.photoURL,
      token: response.token,
      refreshToken: response.refreshToken,
      claims: response.claims,
    });

    // Update user state
    setUser({
      uid: response.uid,
      email: response.email,
      displayName: response.displayName,
      photoURL: response.photoURL,
      claims: response.claims,
    });
  };

  const signUp = async (data: SignUpData) => {
    const response = await sign_up(data);

    // Store auth data
    storeAuth({
      uid: response.uid,
      email: response.email,
      displayName: response.displayName,
      photoURL: response.photoURL,
      token: response.token,
      refreshToken: response.refreshToken,
      claims: response.claims,
    });

    // Update user state
    setUser({
      uid: response.uid,
      email: response.email,
      displayName: response.displayName,
      photoURL: response.photoURL,
      claims: response.claims,
    });
  };

  const signOut = async () => {
    clearAuth();
    setUser(null);
  };

  const getIdToken = async () => {
    const stored = getStoredAuth();
    return stored?.token || null;
  };

  const refreshAuth = async () => {
    const stored = getStoredAuth();
    if (!stored?.refreshToken) {
      throw new Error('No refresh token available');
    }

    // First, refresh band role permissions to use latest templates
    // This updates the server-side claims before we get a new token
    try {
      await refresh_band_permissions();
    } catch (err) {
      console.warn('Failed to refresh band permissions:', err);
      // Continue with token refresh even if this fails
    }

    const response = await refresh_token(stored.refreshToken);

    // Store updated auth data
    storeAuth({
      uid: response.uid,
      email: response.email,
      displayName: response.displayName,
      photoURL: response.photoURL,
      token: response.token,
      refreshToken: response.refreshToken,
      claims: response.claims,
    });

    // Update user state
    setUser({
      uid: response.uid,
      email: response.email,
      displayName: response.displayName,
      photoURL: response.photoURL,
      claims: response.claims,
    });
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    getIdToken,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
